import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createProduct, fetchProduct, updateProduct } from '../../api/productsApi';
import { placeholderSvgDataUrl } from '../../utils/placeholderImage';
import { API_BASE } from '../../config/api';

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #333',
  background: '#0B1020',
  color: '#fff',
  fontSize: 14,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const labelStyle = { color: '#9CA3AF', fontSize: '13px', fontWeight: 500, marginBottom: 4, display: 'block' };

const AdminProductEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === 'new' || !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [activeTab, setActiveTab] = useState('basic');

  const [form, setForm] = useState({
    title: '',
    sku: '',
    brand: 'FACTO ELECTRONICAL',
    price: '',
    mrp: '',
    watt: '',
    watt_options: '',
    color_options: '',
    size_options: '',
    cut_size_options: '',
    description: '',
    features: '',
    highlights: '',
    benefits: '',
    why_choose_us: '',
    package_included: '',
    specs: '',
    downloads: '',
    category: 'all-products',
    status: 'active',
    featured: false,
  });

  const [imageItems, setImageItems] = useState([]);
  const [dbCategories, setDbCategories] = useState([]);
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const response = await fetch(`${API_BASE}/categories`);
        const data = await response.json();
        setDbCategories(Array.isArray(data) ? data : []);
      } catch { }
    };
    fetchCats();
  }, []);

  const categoryOptions = useMemo(() => {
    const opts = dbCategories.map((c) => ({ id: c.slug, name: c.name }));
    if (!opts.find((x) => x.id === 'all-products')) opts.unshift({ id: 'all-products', name: 'All Products' });
    return opts;
  }, [dbCategories]);

  useEffect(() => {
    if (isNew) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const p = await fetchProduct(id);
        if (!mounted) return;
        setForm({
          title: p.title || '',
          sku: p.sku || '',
          brand: p.brand || '',
          price: p.price ?? '',
          mrp: p.mrp ?? '',
          watt: p.watt || '',
          watt_options: Array.isArray(p.watt_options) ? p.watt_options.join(', ') : '',
          color_options: Array.isArray(p.color_options) ? p.color_options.join(', ') : '',
          size_options: Array.isArray(p.size_options) ? p.size_options.join(', ') : '',
          cut_size_options: Array.isArray(p.cut_size_options) ? p.cut_size_options.join(', ') : '',
          description: p.description || '',
          features: Array.isArray(p.features) ? p.features.join('\n') : '',
          highlights: Array.isArray(p.highlights) ? p.highlights.join('\n') : '',
          benefits: Array.isArray(p.benefits) ? p.benefits.join('\n') : '',
          why_choose_us: Array.isArray(p.why_choose_us) ? p.why_choose_us.join('\n') : '',
          package_included: Array.isArray(p.package_included) ? p.package_included.join('\n') : '',
          specs: p.specs ? specsJsonToText(p.specs) : '',
          downloads: Array.isArray(p.downloads) ? p.downloads.join('\n') : '',
          category: p.category || 'all-products',
          status: p.status || 'active',
          featured: Boolean(p.featured),
        });
        setImageItems((Array.isArray(p.images) ? p.images : []).map((url, i) => ({
          key: `existing-${i}-${url}`,
          kind: 'existing',
          url,
        })));
      } catch (e) {
        if (mounted) setErr(e?.message || 'Failed to load product');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id, isNew]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr('');
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('sku', form.sku);
      fd.append('brand', form.brand);
      fd.append('price', String(form.price || 0));
      fd.append('mrp', form.mrp ? String(form.mrp) : '');
      fd.append('watt', form.watt);
      fd.append('watt_options', JSON.stringify(form.watt_options.split(',').map(s => s.trim()).filter(Boolean)));
      fd.append('color_options', JSON.stringify(form.color_options.split(',').map(s => s.trim()).filter(Boolean)));
      fd.append('size_options', JSON.stringify(form.size_options.split(',').map(s => s.trim()).filter(Boolean)));
      fd.append('cut_size_options', JSON.stringify(form.cut_size_options.split(',').map(s => s.trim()).filter(Boolean)));
      fd.append('description', form.description);
      fd.append('features', JSON.stringify(form.features.split('\n').map(s => s.trim()).filter(Boolean)));
      fd.append('highlights', JSON.stringify(form.highlights.split('\n').map(s => s.trim()).filter(Boolean)));
      fd.append('benefits', JSON.stringify(form.benefits.split('\n').map(s => s.trim()).filter(Boolean)));
      fd.append('why_choose_us', JSON.stringify(form.why_choose_us.split('\n').map(s => s.trim()).filter(Boolean)));
      fd.append('package_included', JSON.stringify(form.package_included.split('\n').map(s => s.trim()).filter(Boolean)));

      let specsObj = {};
      try { specsObj = JSON.parse(specsTextToJson(form.specs)); } catch { specsObj = {}; }
      fd.append('specs', JSON.stringify(specsObj));

      fd.append('downloads', JSON.stringify(form.downloads.split('\n').map(s => s.trim()).filter(Boolean)));
      fd.append('category', form.category);
      fd.append('status', form.status);
      fd.append('featured', form.featured ? '1' : '0');

      const newFiles = imageItems.filter((item) => item.kind === 'new').map((item) => item.file);
      newFiles.forEach((f) => fd.append('images[]', f));

      const orderArr = imageItems.map((item) => {
        if (item.kind === 'existing') return item.url;
        return { new: newFiles.indexOf(item.file) };
      });
      fd.append('imageOrder', JSON.stringify(orderArr));

      const saved = isNew ? await createProduct(fd) : await updateProduct(id, fd);
      navigate(`/admin/products/${saved.id}`, { replace: true });
    } catch (e2) {
      setErr(e2?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onFiles = (e) => {
    const next = Array.from(e.target.files || []);
    if (!next.length) return;
    const stamp = Date.now();
    const items = next.map((file, i) => ({ key: `new-${stamp}-${i}-${file.name}`, kind: 'new', file }));
    setImageItems((prev) => [...prev, ...items]);
    e.target.value = '';
  };

  const previews = useMemo(() => {
    const map = {};
    imageItems.forEach((item) => {
      if (item.kind === 'new') map[item.key] = URL.createObjectURL(item.file);
    });
    return map;
  }, [imageItems]);

  useEffect(() => {
    const urls = Object.values(previews);
    return () => { urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [previews]);

  const moveImageTo = (sourceKey, targetKey) => {
    setImageItems((prev) => {
      const from = prev.findIndex((i) => i.key === sourceKey);
      const to = prev.findIndex((i) => i.key === targetKey);
      if (from < 0 || to < 0 || from === to) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const onDragStart = (e, key) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
  };

  const onDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (key !== overKey) setOverKey(key);
  };

  const onDrop = (e, key) => {
    e.preventDefault();
    const source = dragKey || e.dataTransfer.getData('text/plain');
    if (source && source !== key) moveImageTo(source, key);
    setDragKey(null);
    setOverKey(null);
  };

  const onDragEnd = () => {
    setDragKey(null);
    setOverKey(null);
  };

  const removeImage = (key) => {
    setImageItems((prev) => prev.filter((i) => i.key !== key));
  };

  const itemPreview = (item) => (item.kind === 'existing' ? item.url : previews[item.key]);

  const cover = imageItems[0] ? itemPreview(imageItems[0]) : placeholderSvgDataUrl({ width: 900, height: 520, text: form.title || 'Product' });

  const specsJsonToText = (specsObj) => {
    if (!specsObj || typeof specsObj !== 'object') return '';
    const lines = [];
    Object.entries(specsObj).forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        Object.entries(value).forEach(([k, v]) => lines.push(`${k}: ${String(v)}`));
      } else {
        lines.push(`${key}: ${String(value)}`);
      }
    });
    return lines.join('\n');
  };

  const specsTextToJson = (text) => {
    const obj = {};
    String(text || '').split('\n').forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const idx = trimmed.indexOf(':');
      const key = (idx > -1 ? trimmed.slice(0, idx) : trimmed).trim();
      const value = idx > -1 ? trimmed.slice(idx + 1).trim() : '';
      if (key) obj[key] = value;
    });
    return JSON.stringify(obj);
  };

  const set = (field) => (e) => setForm((s) => ({ ...s, [field]: e.target.value }));
  const setBool = (field) => (e) => setForm((s) => ({ ...s, [field]: e.target.checked }));

  const tabs = [
    { key: 'basic', label: 'Basic' },
    { key: 'description', label: 'Description' },
    { key: 'keyfeatures', label: 'Key Features' },
    { key: 'highlights', label: 'Product Highlights' },
    { key: 'specs', label: 'Technical Specification' },
    { key: 'benefits', label: 'Product Benefits' },
    { key: 'whychoose', label: 'Why Choose Us' },
    { key: 'package', label: 'Package Included' },
  ];

  const tabStyle = (key) => ({
    padding: '8px 16px',
    borderRadius: 20,
    border: 'none',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'inherit',
    background: activeTab === key ? '#FACC15' : 'rgba(255,255,255,0.08)',
    color: activeTab === key ? '#0B1020' : '#9CA3AF',
    transition: 'all 0.2s ease',
  });

  return (
    <div className="container" style={{ padding: '2rem 0' }}>
      <style>{`
        .prod-edit-grid { display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 0.75rem; margin-top: 0.5rem; }
        .prod-edit-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .prod-edit-header { display: flex; justify-content: space-between; align-items: center; gap: 4px; flex-wrap: wrap; margin-bottom: 4px; }
        @media (max-width: 768px) {
          .prod-edit-grid { grid-template-columns: 1fr; }
          .prod-edit-field-row { grid-template-columns: 1fr; }
          .prod-edit-header { flex-direction: column; align-items: stretch; gap: 2px; }
          .prod-edit-header > button { width: 100%; text-align: center; }
        }
      `}</style>
      <div className="prod-edit-header">
        <div>
          <h1 style={{ margin: 0, color: '#F9FAFB', lineHeight: 1.2 }}>{isNew ? 'Add Product' : 'Edit Product'}</h1>
          <p style={{ margin: 0, color: '#6B7280', fontSize: '13px', lineHeight: 1.3 }}>Fill all relevant fields</p>
        </div>
        <button className="btn-secondary" type="button" onClick={() => navigate('/admin/products')} style={{ border: '1px solid rgba(255,215,0,0.2)', color: '#FFD700', background: 'transparent', padding: '8px 16px', borderRadius: 8, cursor: 'pointer' }}>
          Back to Products
        </button>
      </div>

      {err && (
        <div className="card" style={{ marginTop: '1rem', borderColor: '#5b1a1a', padding: '12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)' }}>
          <div style={{ color: '#ffb3b3' }}>{err}</div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: '2rem 0', color: '#6B7280' }}>Loading...</div>
      ) : (
        <form onSubmit={submit} className="prod-edit-grid">
          <div className="card" style={{ margin: 0, background: '#0F1B2D', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: '1.5rem' }}>
            {/* Tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {tabs.map(t => (
                <button key={t.key} type="button" onClick={() => setActiveTab(t.key)} style={tabStyle(t.key)}>{t.label}</button>
              ))}
            </div>

            {/* Basic Tab */}
            {activeTab === 'basic' && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gap: 4 }}>
                  <label style={labelStyle}>Product Title *</label>
                  <input style={inputStyle} required value={form.title} onChange={set('title')} placeholder="e.g. COB Spot Light Pro" />
                </div>
                <div className="prod-edit-field-row">
                  <div style={{ display: 'grid', gap: 4 }}>
                    <label style={labelStyle}>SKU</label>
                    <input style={inputStyle} value={form.sku} onChange={set('sku')} placeholder="e.g. CSP-12W-BK" />
                  </div>
                  <div style={{ display: 'grid', gap: 4 }}>
                    <label style={labelStyle}>Brand</label>
                    <input style={inputStyle} value={form.brand} onChange={set('brand')} placeholder="FACTO ELECTRONICAL" />
                  </div>
                </div>
                <div className="prod-edit-field-row">
                  <div style={{ display: 'grid', gap: 4 }}>
                    <label style={labelStyle}>Price (₹)</label>
                    <input style={inputStyle} type="number" min="0" step="1" value={form.price} onChange={set('price')} placeholder="Selling price" />
                  </div>
                  <div style={{ display: 'grid', gap: 4 }}>
                    <label style={labelStyle}>MRP (₹)</label>
                    <input style={inputStyle} type="number" min="0" step="1" value={form.mrp} onChange={set('mrp')} placeholder="Original price" />
                  </div>
                </div>
                <div className="prod-edit-field-row">
                  <div style={{ display: 'grid', gap: 4 }}>
                    <label style={labelStyle}>Category</label>
                    <select style={inputStyle} value={form.category} onChange={set('category')}>
                      {categoryOptions.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="prod-edit-field-row">
                  <div style={{ display: 'grid', gap: 4 }}>
                    <label style={labelStyle}>Status</label>
                    <select style={inputStyle} value={form.status} onChange={set('status')}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'end', gap: 12, paddingBottom: 4 }}>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#ccc', fontSize: 14, cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.featured} onChange={setBool('featured')} />
                      Featured
                    </label>
                  </div>
                </div>
                <div style={{ display: 'grid', gap: 4 }}>
                  <label style={labelStyle}>Product Images</label>
                  <input type="file" multiple accept="image/*" onChange={onFiles} style={{ color: '#ccc' }} />
                  <div style={{ color: '#6B7280', fontSize: 13 }}>First image becomes the cover. Drag and drop to reorder, and ✕ to delete an image.</div>
                  {imageItems.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                        {imageItems.map((item, idx) => (
                          <div
                            key={item.key}
                            draggable
                            onDragStart={(e) => onDragStart(e, item.key)}
                            onDragOver={(e) => onDragOver(e, item.key)}
                            onDrop={(e) => onDrop(e, item.key)}
                            onDragEnd={onDragEnd}
                            style={{
                              width: 140,
                              background: overKey === item.key && dragKey && dragKey !== item.key ? 'rgba(250,204,21,0.15)' : 'rgba(255,255,255,0.06)',
                              borderRadius: 10,
                              padding: 6,
                              border: overKey === item.key && dragKey && dragKey !== item.key ? '1px dashed #FACC15' : '1px solid transparent',
                              cursor: 'grab',
                              opacity: dragKey === item.key ? 0.45 : 1,
                            }}
                          >
                            <div style={{ position: 'relative', height: 90, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
                              <img src={itemPreview(item)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }} />
                              {idx === 0 && (
                                <span style={{ position: 'absolute', top: 4, left: 4, background: '#FACC15', color: '#0B1020', fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '1px 6px' }}>Cover</span>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                              <span style={{ color: '#9CA3AF', fontSize: 12, cursor: 'grab' }} title="Drag to reorder">&#9776;</span>
                              <button type="button" onClick={() => removeImage(item.key)} title="Delete image" style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: 6, fontSize: 12, cursor: 'pointer', padding: '2px 8px', lineHeight: 1 }}>&times;</button>
                            </div>
                            <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.kind === 'new' ? item.file.name : 'Existing image'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Description Tab */}
            {activeTab === 'description' && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gap: 4 }}>
                  <label style={labelStyle}>Description</label>
                  <textarea style={{ ...inputStyle, minHeight: 200, resize: 'vertical', lineHeight: 1.6 }} value={form.description} onChange={set('description')} placeholder="Write a detailed product description..." />
                </div>
                <div style={{ display: 'grid', gap: 4 }}>
                  <label style={labelStyle}>Download URLs (one per line)</label>
                  <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical', fontFamily: 'monospace' }} value={form.downloads} onChange={set('downloads')} placeholder="https://example.com/catalog.pdf" />
                </div>
              </div>
            )}

            {/* Key Features Tab */}
            {activeTab === 'keyfeatures' && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gap: 4 }}>
                  <label style={labelStyle}>Key Features (one per line)</label>
                  <textarea style={{ ...inputStyle, minHeight: 250, resize: 'vertical', fontFamily: 'monospace', lineHeight: 1.6 }} value={form.features} onChange={set('features')} placeholder="Energy efficient LED technology&#10;Long lifespan up to 50,000 hours&#10;IP65 Waterproof rating&#10;Easy installation" />
                </div>
              </div>
            )}

            {/* Product Highlights Tab */}
            {activeTab === 'highlights' && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gap: 4 }}>
                  <label style={labelStyle}>Product Highlights (one per line)</label>
                  <textarea style={{ ...inputStyle, minHeight: 250, resize: 'vertical', fontFamily: 'monospace', lineHeight: 1.6 }} value={form.highlights} onChange={set('highlights')} placeholder="Superior brightness with high CRI&#10;Premium aluminum body&#10;Advanced heat dissipation" />
                </div>
              </div>
            )}

            {/* Product Benefits Tab */}
            {activeTab === 'benefits' && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gap: 4 }}>
                  <label style={labelStyle}>Product Benefits (one per line)</label>
                  <textarea style={{ ...inputStyle, minHeight: 250, resize: 'vertical', fontFamily: 'monospace', lineHeight: 1.6 }} value={form.benefits} onChange={set('benefits')} placeholder="Save up to 75% on electricity bills&#10;Lower maintenance costs&#10;Better visibility and safety" />
                </div>
              </div>
            )}

            {/* Why Choose Us Tab */}
            {activeTab === 'whychoose' && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gap: 4 }}>
                  <label style={labelStyle}>Why Choose Us (one per line)</label>
                  <textarea style={{ ...inputStyle, minHeight: 250, resize: 'vertical', fontFamily: 'monospace', lineHeight: 1.6 }} value={form.why_choose_us} onChange={set('why_choose_us')} placeholder="Trusted by 10,000+ customers&#10;5 year warranty on all products&#10;Pan India service network" />
                </div>
              </div>
            )}

            {/* Package Included Tab */}
            {activeTab === 'package' && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gap: 4 }}>
                  <label style={labelStyle}>Package Included (one per line)</label>
                  <textarea style={{ ...inputStyle, minHeight: 250, resize: 'vertical', fontFamily: 'monospace', lineHeight: 1.6 }} value={form.package_included} onChange={set('package_included')} placeholder="1 x LED Downlight&#10;1 x Mounting Bracket&#10;1 x User Manual&#10;1 x Warranty Card" />
                </div>
              </div>
            )}

            {/* Specs Tab */}
            {activeTab === 'specs' && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gap: 4 }}>
                  <label style={labelStyle}>Technical Specifications (Field: Value — one per line)</label>
                  <textarea style={{ ...inputStyle, minHeight: 300, resize: 'vertical', fontFamily: 'monospace', lineHeight: 1.6 }} value={form.specs} onChange={set('specs')} placeholder="Wattage: 12W&#10;Voltage Range: 100-240V AC&#10;Material: Aluminum&#10;IP Rating: IP65&#10;Warranty: 3 Years" />
                </div>
              </div>
            )}

            {/* Save Button */}
            <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
              <button className="btn" type="submit" disabled={saving} style={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', color: '#0B1020', border: 'none', padding: '10px 24px', borderRadius: 8, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : isNew ? 'Save Product' : 'Update Product'}
              </button>
              {!isNew && (
                <button className="btn-secondary" type="button" onClick={() => navigate(`/product/${id}`)} style={{ border: '1px solid rgba(255,215,0,0.2)', color: '#FFD700', background: 'transparent', padding: '10px 20px', borderRadius: 8, cursor: 'pointer' }}>
                  View on site
                </button>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="card" style={{ margin: 0, alignSelf: 'start', background: '#0F1B2D', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: '1.5rem' }}>
            <div style={{ fontWeight: 700, marginBottom: '0.75rem', color: '#FACC15' }}>Preview</div>
            <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)' }}>
              <img src={cover} alt="Cover" style={{ width: '100%', height: 240, objectFit: 'cover', display: 'block' }} />
            </div>
            {imageItems.length > 1 && (
              <>
                <div style={{ marginTop: '0.75rem', color: '#6B7280', fontSize: 13 }}>Gallery</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 8 }}>
                  {imageItems.slice(0, 6).map((item) => (
                    <div key={item.key} style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <img src={itemPreview(item)} alt="" style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default AdminProductEdit;
